import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import BarcodeScanModal from './components/BarcodeScanModal';
import api from '../../utils/api';
import { toast } from 'sonner';

const Checkout = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [isListening, setIsListening] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);
  const barcodeTimeoutRef = useRef(null);
  const lastKeypressTimeRef = useRef(0);

  useEffect(() => {
    let active = true;
    const fetchProducts = async () => {
      try {
        const { data } = await api.get('/products');
        if (!Array.isArray(data)) throw new Error('Product response has an unexpected shape');
        if (active) {
          setProducts(data);
          setProductsError('');
        }
      } catch (err) {
        console.error('[Checkout] Error fetching products for search:', err);
        if (active) setProductsError('Products could not be loaded. Try again or use the barcode scanner.');
      } finally {
        if (active) setProductsLoading(false);
      }
    };

    fetchProducts();
    return () => {
      active = false;
    };
  }, []);

  // Fetch cart items on component mount
  useEffect(() => {
    fetchCartItems();
    
    // Set up polling to refresh cart items every 5 seconds
    const intervalId = setInterval(() => {
      fetchCartItems();
    }, 5000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Fetch cart items from the database
  const fetchCartItems = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get('/cart');
      setItems(data);
    } catch (err) {
      console.error('[Checkout] Error fetching cart items:', err);
      toast.error('Error loading cart items');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const total = subtotal;

  // Handle keyboard input for barcode scanners
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isListening || isScanModalOpen || isProcessing) return;
      if (e.target instanceof HTMLElement && e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      
      const now = Date.now();
      const keyDelay = now - lastKeypressTimeRef.current;
      lastKeypressTimeRef.current = now;
      
      // Most barcode scanners type characters rapidly (typically < 50ms between keypresses)
      const isRapidInput = keyDelay < 50;
      
      // Only process alphanumeric keys and common barcode characters
      if (/[\w\d\-_]/.test(e.key) && e.key.length === 1) {
        // Clear timeout and restart
        if (barcodeTimeoutRef.current) {
          clearTimeout(barcodeTimeoutRef.current);
        }
        
        // Add character to buffer
        setBarcodeBuffer(prev => prev + e.key);
        
        // Set timeout to process barcode after a short delay
        barcodeTimeoutRef.current = setTimeout(() => {
          if (barcodeBuffer.length > 5) { // Most barcodes are at least 6 chars
            console.log('[Checkout] Processing keyboard barcode:', barcodeBuffer);
            lookupBarcodeFromKeyboard(barcodeBuffer);
          }
          setBarcodeBuffer('');
        }, 300); // Wait 300ms after last character
      } else if (e.key === 'Enter' && barcodeBuffer.length > 0) {
        // Enter key often signals end of barcode scan
        e.preventDefault();
        if (barcodeTimeoutRef.current) {
          clearTimeout(barcodeTimeoutRef.current);
        }
        console.log('[Checkout] Processing keyboard barcode (Enter):', barcodeBuffer);
        lookupBarcodeFromKeyboard(barcodeBuffer);
        setBarcodeBuffer('');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
    };
  }, [barcodeBuffer, isListening, isScanModalOpen, isProcessing]);
  
  // Look up barcode from keyboard input
  const lookupBarcodeFromKeyboard = async (barcode) => {
    if (!barcode || barcode.length < 6) return;
    
    try {
      const toastId = toast.loading(`Looking up barcode: ${barcode}`);
      const { data } = await api.get(`/barcode/${encodeURIComponent(barcode)}`);
      
      if (data && data.id) {
        toast.dismiss(toastId);
        handleBarcodeScanned(barcode, data);
      } else {
        toast.error(`Product not found: ${barcode}`, {
          description: "Please add this product to your inventory first.",
          action: {
            label: "Add Product",
            onClick: () => navigate('/product-catalog?addProduct=true&barcode=' + barcode)
          }
        });
      }
    } catch (err) {
      console.error('[Checkout] Barcode lookup error:', err);
      toast.error('Error looking up product', {
        description: err.response?.data?.message || "Please try again or add the product manually",
        action: {
          label: "Add Product",
          onClick: () => navigate('/product-catalog?addProduct=true')
        }
      });
    }
  };

  const matchingProducts = productSearch.trim()
    ? products.filter(product => (
      product.name?.toLowerCase().includes(productSearch.trim().toLowerCase())
      || product.barcode?.toLowerCase().includes(productSearch.trim().toLowerCase())
    )).slice(0, 8)
    : [];

  const handleBarcodeScanned = async (barcode, productData) => {
    console.log('[Checkout] ========== BARCODE SCANNED ==========');
    console.log('[Checkout] Barcode:', barcode);
    console.log('[Checkout] Product Data:', productData);
    
    if (!productData) {
      console.error('[Checkout] No product data received!');
      toast.error('Product not found in inventory');
      return;
    }

    // Validate product has required fields
    if (!productData.id) {
      console.error('[Checkout] Product missing ID!');
      toast.error('Invalid product data');
      return;
    }

    // Check stock availability
    const availableStock = Number(productData.quantity ?? productData.availableStock);
    if (!Number.isFinite(availableStock) || availableStock <= 0) {
      console.warn('[Checkout] Product is out of stock, quantity:', productData.quantity);
      toast.error('Product is out of stock');
      return;
    }

    try {
      // Check if product already exists in cart
      const existingItemIndex = items.findIndex(item => item.productId === productData.id || item.id === productData.id);
      let cartItem;
      
      if (existingItemIndex >= 0) {
        // Increment quantity in cart database
        const currentQty = items[existingItemIndex].quantity;
        
        if (currentQty >= availableStock) {
          toast.warning(`Cannot add more. Only ${availableStock} in stock.`);
          return;
        }
        
        // Update cart in database
        const { data } = await api.put(`/cart/${items[existingItemIndex].id}`, { 
          quantity: currentQty + 1 
        });
        cartItem = data;
        
        toast.success(`Increased quantity of ${productData.name}`);
      } else {
        // Add new item to cart database with all product details
        const newItem = {
          id: productData.id,
          productId: productData.id,
          name: productData.name || 'Unknown Product',
          barcode: productData.barcode || barcode,
          price: productData.sellingPrice || productData.price || 0,
          quantity: 1,
          imageUrl: productData.imageUrl || null,
          availableStock: productData.quantity || 0,
          // Include all other product information
          sku: productData.sku || '',
          category: productData.category || [],
          description: productData.description || '',
          cost: productData.cost || 0,
          dimensions: productData.dimensions || null,
          weight: productData.weight || null,
          supplier: productData.supplier || '',
          locations: productData.locations || [],
          reorderPoint: productData.reorderPoint || 0,
          isActive: productData.isActive !== undefined ? productData.isActive : true,
          trackInventory: productData.trackInventory !== undefined ? productData.trackInventory : true,
        };
        
        const { data } = await api.post('/cart', { product: newItem, quantity: 1 });
        cartItem = data;
        toast.success(`Added ${productData.name} to cart`);
      }
      
      // Update local state immediately instead of waiting for database refresh
      if (existingItemIndex >= 0) {
        setItems(previousItems => previousItems.map(item => (
          item.id === cartItem.id ? cartItem : item
        )));
      } else {
        setItems(previousItems => [...previousItems, cartItem]);
      }
    } catch (error) {
      console.error('[Checkout] Error updating cart:', error);
      toast.error('Failed to update cart');
    }
    
    console.log('[Checkout] ========== END BARCODE SCANNED ==========');
  };

  const handleQuantityChange = async (itemId, newQuantity) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }
    
    const remaining = (item.remaining ?? item.availableStock ?? 0);
    if (newQuantity > remaining) {
      toast.warning(`Requested ${newQuantity} exceeds remaining ${remaining}`);
      return;
    }
    
    try {
      await api.put(`/cart/${itemId}`, { quantity: newQuantity });
      fetchCartItems();
    } catch (error) {
      console.error('[Checkout] Error updating quantity:', error);
      toast.error('Failed to update quantity');
    }
  };

  const handleRemoveItem = async (itemId) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    
    try {
      await api.delete(`/cart/${itemId}`);
      toast.info(`Removed ${item?.name || 'item'} from checkout`);
      fetchCartItems();
    } catch (error) {
      console.error('[Checkout] Error removing item:', error);
      toast.error('Failed to remove item');
    }
  };

  const handleClearCart = async () => {
    if (items.length === 0) return;
    
    if (window.confirm('Are you sure you want to clear all items?')) {
      try {
        await api.delete('/cart');
        toast.info('Cart cleared');
        fetchCartItems();
      } catch (error) {
        console.error('[Checkout] Error clearing cart:', error);
        toast.error('Failed to clear cart');
      }
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if inside input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Alt+S: Open scanner
      if (e.altKey && e.key === 's') {
        e.preventDefault();
        setIsScanModalOpen(true);
      }
      
      // Alt+C: Clear cart
      if (e.altKey && e.key === 'c') {
        e.preventDefault();
        if (items.length > 0) handleClearCart();
      }
      
      // Alt+P: Toggle barcode listening
      if (e.altKey && e.key === 'p') {
        e.preventDefault();
        setIsListening(prev => !prev);
      }
      
      // Alt+Enter: Complete checkout
      if (e.altKey && e.key === 'Enter') {
        e.preventDefault();
        if (items.length > 0 && !isProcessing) handleCheckout();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, isProcessing]);

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Process checkout through the new checkout API
      const checkoutData = {
        items: items.map(item => ({
          productId: item.id || item.productId,
          name: item.name,
          barcode: item.barcode,
          quantity: item.quantity,
          price: item.price,
          subtotal: item.price * item.quantity,
        })),
        subtotal,
        total,
        customerName: customerName || 'Walk-in Customer',
        customerEmail: customerEmail || null,
        timestamp: new Date().toISOString(),
        paymentStatus: 'completed',
      };

      // Process checkout - this will update inventory and clear cart
      const { data: checkout } = await api.post('/checkout', checkoutData);
      console.log('[Checkout] Checkout completed:', checkout);

      toast.success('Checkout completed successfully!');
      
      // Clear customer info and refresh cart (should be empty now)
      setCustomerName('');
      setCustomerEmail('');
      fetchCartItems();
    } catch (error) {
      console.error('[Checkout] Error processing checkout:', error);
      toast.error('Failed to process checkout. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <Icon name="ArrowLeft" size={20} />
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Checkout</h1>
          </div>
          
          {/* Mobile: Scan Barcode button in its own row */}
          <div className="mb-2 sm:hidden">
            <Button
              variant="default"
              onClick={() => setIsScanModalOpen(true)}
              iconName="Scan"
              iconPosition="left"
              iconSize={18}
              title="Open scanner (Alt+S)"
              className="w-full"
            >
              Scan Barcode
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-muted-foreground">
              Scan barcodes to add items to checkout
            </p>
            <div className="flex items-center justify-between sm:justify-end space-x-2">
              <div className="hidden sm:flex items-center">
                <Button
                  variant="outline"
                  onClick={handleClearCart}
                  disabled={items.length === 0}
                  iconName="Trash2"
                  iconPosition="left"
                  iconSize={16}
                  title="Clear cart (Alt+C)"
                >
                  Clear Cart
                </Button>
                <Button
                  variant="default"
                  onClick={() => setIsScanModalOpen(true)}
                  iconName="Scan"
                  iconPosition="left"
                  iconSize={18}
                  title="Open scanner (Alt+S)"
                >
                  Scan Barcode
                </Button>
              </div>
              <div className="flex items-center">
                <div className={`flex items-center px-3 py-1 rounded-full text-xs ${isListening ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${isListening ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  {isListening ? 'Barcode Ready' : 'Barcode Paused'}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsListening(!isListening)}
                  className="ml-2"
                  title={isListening ? "Pause barcode input (Alt+P)" : "Resume barcode input (Alt+P)"}
                >
                  {isListening ? <Icon name="Pause" size={14} /> : <Icon name="Play" size={14} />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div
          className="relative z-20 mb-6"
          onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsProductSearchOpen(false);
          }}
        >
          <label htmlFor="checkout-product-search" className="mb-2 block text-sm font-medium text-foreground">
            Find a product by name or barcode
          </label>
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="checkout-product-search"
              type="search"
              autoComplete="off"
              placeholder="Search product name or barcode..."
              value={productSearch}
              onFocus={() => setIsProductSearchOpen(true)}
              onChange={event => {
                setProductSearch(event.target.value);
                setIsProductSearchOpen(true);
              }}
              className="pl-10"
              aria-controls="checkout-product-search-results"
              aria-expanded={isProductSearchOpen && Boolean(productSearch.trim())}
            />
          </div>
          {isProductSearchOpen && productSearch.trim() && (
            <div id="checkout-product-search-results" role="listbox" className="absolute left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
              {productsLoading ? (
                <p className="p-4 text-sm text-muted-foreground">Loading products...</p>
              ) : productsError ? (
                <div className="p-4">
                  <p role="alert" className="text-sm text-destructive">{productsError}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={async () => {
                      setProductsLoading(true);
                      setProductsError('');
                      try {
                        const { data } = await api.get('/products');
                        if (!Array.isArray(data)) throw new Error('Product response has an unexpected shape');
                        setProducts(data);
                      } catch (err) {
                        console.error('[Checkout] Error retrying product search:', err);
                        setProductsError('Products could not be loaded. Try again or use the barcode scanner.');
                      } finally {
                        setProductsLoading(false);
                      }
                    }}
                  >
                    Retry
                  </Button>
                </div>
              ) : matchingProducts.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No products match that name or barcode.</p>
              ) : matchingProducts.map(product => (
                <button
                  key={product.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="flex w-full items-center justify-between gap-4 border-b border-border p-3 text-left last:border-b-0 hover:bg-muted/60 focus:bg-muted/60 focus:outline-none"
                  onClick={() => {
                    handleBarcodeScanned(product.barcode || '', product);
                    setProductSearch('');
                    setIsProductSearchOpen(false);
                  }}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">{product.name || 'N/A'}</span>
                    <span className="block truncate text-xs text-muted-foreground">Barcode: {product.barcode || 'N/A'}</span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    Stock: {product.quantity ?? 'N/A'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Items List */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-lg shadow-sm">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground flex items-center">
                  <Icon name="ShoppingCart" size={20} className="mr-2" />
                  Cart Items ({items.length})
                </h2>
              </div>
              
              <div className="p-4">
                {items.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="ShoppingCart" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground text-lg mb-2">Your cart is empty</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Click "Scan Barcode" to add items
                    </p>
                    <Button
                      variant="default"
                      onClick={() => setIsScanModalOpen(true)}
                      iconName="Scan"
                      iconPosition="left"
                      iconSize={18}
                    >
                      Start Scanning
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center space-x-4 p-3 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-colors"
                      >
                        {/* Product Image */}
                        <div className="w-16 h-16 bg-background rounded-md flex items-center justify-center overflow-hidden flex-shrink-0">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Icon name="Package" size={32} className="text-muted-foreground" />
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground truncate">
                            {item.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            ${item.price.toFixed(2)} each
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Stock: {item.remaining ?? item.availableStock ?? 0}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="h-8 w-8"
                          >
                            <Icon name="Minus" size={14} />
                          </Button>
                          <span className="w-12 text-center font-semibold text-foreground">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="h-8 w-8"
                            disabled={item.quantity >= (item.remaining ?? item.availableStock ?? 0)}
                          >
                            <Icon name="Plus" size={14} />
                          </Button>
                        </div>

                        {/* Item Total */}
                        <div className="text-right min-w-[80px]">
                          <p className="font-semibold text-foreground">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>

                        {/* Remove Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-destructive hover:text-destructive h-8 w-8"
                        >
                          <Icon name="X" size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Checkout Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg shadow-sm sticky top-6">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground">
                  Order Summary
                </h2>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Customer Info */}
                <div className="space-y-3">
                  <Input
                    label="Customer Name (Optional)"
                    type="text"
                    placeholder="Enter customer name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                  <Input
                    label="Customer Email (Optional)"
                    type="email"
                    placeholder="customer@example.com"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </div>

                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium text-foreground">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between">
                    <span className="font-semibold text-foreground">Total</span>
                    <span className="font-bold text-xl text-primary">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  variant="default"
                  onClick={handleCheckout}
                  disabled={items.length === 0 || isProcessing}
                  loading={isProcessing}
                  iconName="CreditCard"
                  iconPosition="left"
                  iconSize={18}
                  className="w-full"
                >
                  {isProcessing ? 'Processing...' : 'Complete Checkout'}
                </Button>

                {items.length > 0 && (
                  <div className="text-xs text-muted-foreground text-center">
                    {items.length} item{items.length !== 1 ? 's' : ''} in cart
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scan Modal */}
      <BarcodeScanModal
        isOpen={isScanModalOpen}
        onClose={() => setIsScanModalOpen(false)}
        onBarcodeScanned={handleBarcodeScanned}
      />
    </div>
  );
};

export default Checkout;
